import { FormEvent, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import {
  LocationModal,
  emptyLocationFormState,
  locationToFormState,
  type CompanyLocationFormState,
  type LocationFieldErrors,
} from '../admin/clients/clients-page';
import type { CompanyLocationRecord } from '../admin/clients/api';

type RawLocationRecord = Omit<CompanyLocationRecord, 'services_count'> & {
  services_count?: number;
  group_id: number | null;
  group?: { id: number; name: string; slug: string } | null;
  services?: Array<{ id: number }>;
  aliases?: Array<{ id: number; alias: string }>;
};

export type ScopedLocationRecord = CompanyLocationRecord & {
  group_id: number | null;
  group: { id: number; name: string; slug: string } | null;
  aliases?: Array<{ id: number; alias: string }>;
};

type LocationIndexResponse = {
  data: {
    data: RawLocationRecord[];
  };
};

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

const normalizeLocation = (record: RawLocationRecord): ScopedLocationRecord => ({
  ...record,
  group: record.group ?? null,
  services_count: record.services_count ?? record.services?.length ?? 0,
});

const sortLocations = (records: ScopedLocationRecord[]) =>
  [...records].sort((left, right) => left.display_name.localeCompare(right.display_name));

const readFailure = async (response: Response, fallback: string) => {
  const payload = await response.json().catch(() => null) as { message?: string; errors?: Record<string, string[]> } | null;
  const details = payload?.errors ? Object.values(payload.errors).flat().join(' ') : null;
  return details || payload?.message || fallback;
};

type ReviewContext = {
  locations: ScopedLocationRecord[];
  loading: boolean;
  busyRecordId: number | null;
  deleteConfirmId: number | null;
  addLocation: () => void;
  editLocation: (record: ScopedLocationRecord) => void;
  deleteLocation: (record: ScopedLocationRecord) => void;
};

export const LocationHelpTip = () => (
  <Alert variant="info" className="mb-0">
    <p className="mb-2"><strong>Company locations are read-only here.</strong> Scheduler-added locations apply only to the selected Schedule Group and do not change Company Admin records.</p>
    <p className="mb-0">Hospital and other client-wide locations are sent to Company Admin for review instead of being created directly in the Schedule Group.</p>
  </Alert>
);

export default function GroupLocationWorkspace({ tenantQuery, reviewWorkspace }: { tenantQuery: string; reviewWorkspace?: ReactNode | ((context: ReviewContext) => ReactNode) }) {
  const [locations, setLocations] = useState<ScopedLocationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyRecordId, setBusyRecordId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState<CompanyLocationFormState>(emptyLocationFormState);
  const [fieldErrors, setFieldErrors] = useState<LocationFieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams(tenantQuery);
    params.set('per_page', '100');
    return params.toString();
  }, [tenantQuery]);

  const loadLocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/secure/schedule-settings/locations?${query}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(await readFailure(response, `Unable to load locations (${response.status}).`));
      const payload = await response.json() as LocationIndexResponse;
      setLocations(sortLocations(payload.data.data.map(normalizeLocation)));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to load locations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadLocations();
  }, [query]);

  const saveLocationRecord = async (
    payload: Record<string, string | boolean>,
    id: number | null = null
  ): Promise<{ record: ScopedLocationRecord | null; routedToCompanyAdmin: boolean }> => {
    const response = await fetch(
      id === null
        ? `/api/v1/secure/schedule-settings/locations?${tenantQuery}`
        : `/api/v1/secure/schedule-settings/locations/${id}?${tenantQuery}`,
      {
        method: id === null ? 'POST' : 'PATCH',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) throw new Error(await readFailure(response, `Unable to save location (${response.status}).`));
    const result = await response.json() as { record?: RawLocationRecord; routed_to_company_admin?: boolean };
    return {
      record: result.record ? normalizeLocation(result.record) : null,
      routedToCompanyAdmin: Boolean(result.routed_to_company_admin),
    };
  };

  const saveLocation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    setFieldErrors({});
    try {
      const result = await saveLocationRecord({
        code: draft.code.trim(),
        display_name: draft.display_name.trim(),
        public_label: draft.public_label.trim(),
        location_group: draft.location_group.trim(),
        timezone: draft.timezone.trim(),
        is_active: draft.is_active,
        notes: draft.notes.trim(),
      }, draft.id);
      if (result.record) setLocations(current => sortLocations([...current.filter(item => item.id !== result.record!.id), result.record!]));
      setShowModal(false);
      setSuccess(result.routedToCompanyAdmin
        ? 'Location sent to Company Admin for client-wide review.'
        : draft.id === null ? 'Schedule Group location created.' : 'Schedule Group location updated.');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to save location.');
    } finally {
      setSaving(false);
    }
  };

  const deleteLocation = async (record: ScopedLocationRecord) => {
    if (record.group_id === null) return;
    if (deleteConfirmId !== record.id) {
      setDeleteConfirmId(record.id);
      return;
    }
    setBusyRecordId(record.id);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`/api/v1/secure/schedule-settings/locations/${record.id}?${tenantQuery}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrfToken },
      });
      if (!response.ok) throw new Error(await readFailure(response, `Unable to delete location (${response.status}).`));
      setLocations(current => current.filter(item => item.id !== record.id));
      setDeleteConfirmId(null);
      setSuccess('Schedule Group location deleted.');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to delete location.');
    } finally {
      setBusyRecordId(null);
    }
  };

  const editLocation = (record: ScopedLocationRecord) => {
    setDraft(locationToFormState(record));
    setFieldErrors({});
    setShowModal(true);
  };

  const addLocation = () => {
    setDraft(emptyLocationFormState);
    setFieldErrors({});
    setShowModal(true);
  };

  return (
    <div className="d-flex flex-column gap-3">
      {error ? <Alert variant="danger" className="mb-0">{error}</Alert> : null}
      {success ? <Alert variant="success" className="mb-0">{success}</Alert> : null}

      {!reviewWorkspace ? <Card className="custom-card border mb-0">
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div className="card-title mb-0">Locations</div>
          <Button variant="primary" size="sm" className="btn-wave" onClick={addLocation}>
            <i className="ri-add-line me-1" /> Add location
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 align-middle">
            <thead>
              <tr><th>Code</th><th>Display Name</th><th>Scope</th><th>Status</th><th>Services</th><th className="text-end">Actions</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-5"><Spinner animation="border" size="sm" className="me-2" />Loading locations</td></tr>
              ) : locations.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-5 text-muted">No locations are available for this Schedule Group.</td></tr>
              ) : locations.map(record => {
                const companyManaged = record.group_id === null;
                return (
                  <tr key={record.id}>
                    <td className="fw-semibold">{record.code}</td>
                    <td><div>{record.display_name}</div>{record.notes ? <div className="text-muted fs-12 mt-1">{record.notes}</div> : null}</td>
                    <td><Badge bg="" className={companyManaged ? 'bg-primary-transparent text-primary' : 'bg-info-transparent text-info'}>{companyManaged ? 'Company Admin' : record.group?.name ?? 'Schedule Group'}</Badge></td>
                    <td><Badge bg={record.is_active ? 'success' : 'secondary'}>{record.is_active ? 'Active' : 'Inactive'}</Badge></td>
                    <td>{record.services_count}</td>
                    <td className="text-end">
                      {companyManaged ? <span className="text-muted fs-12">Read only</span> : <div className="d-inline-flex gap-2">
                        <Button size="sm" variant="light" className="btn-wave" onClick={() => {
                          editLocation(record);
                        }}>Edit</Button>
                        {deleteConfirmId === record.id ? <Button size="sm" variant="light" className="btn-wave" disabled={busyRecordId === record.id} onClick={() => setDeleteConfirmId(null)}>Cancel</Button> : null}
                        <Button size="sm" variant="outline-danger" className="btn-wave" disabled={busyRecordId === record.id} onClick={() => void deleteLocation(record)}>{busyRecordId === record.id ? 'Deleting…' : deleteConfirmId === record.id ? 'Confirm delete' : 'Delete'}</Button>
                      </div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card> : null}

      {typeof reviewWorkspace === 'function'
        ? reviewWorkspace({ locations, loading, busyRecordId, deleteConfirmId, addLocation, editLocation, deleteLocation: record => void deleteLocation(record) })
        : reviewWorkspace}

      <LocationModal
        draft={draft}
        fieldErrors={fieldErrors}
        saving={saving}
        show={showModal}
        onChange={(field, value) => setDraft(current => ({ ...current, [field]: value }))}
        onHide={() => setShowModal(false)}
        onSubmit={saveLocation}
      />
    </div>
  );
}
